import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { brandName } from '#app/components/branding.js'
import { reactionTypes } from '#app/components/ui/reactions.js'
import { prisma } from '#app/utils/db.server.js'
import { publicProfileReactionFilter } from '#app/utils/reactions.server.js'
import { type loader as reactionsLoader } from './reactions.tsx'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			Reaction: {
				select: { id: true, type: true, commentId: true, scriptId: true, guideId: true, buildId: true, newsPostId: true, fanSiteId: true, createdAt: true },
				where: { type: params.type, ...publicProfileReactionFilter },
				orderBy: { createdAt: 'desc' },
			},
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Not found', { status: 404 })

	const uniqueScriptIds = owner.Reaction.map((r) => r.scriptId).filter(Boolean) as string[]
	const uniqueGuideIds = owner.Reaction.map((r) => r.guideId).filter(Boolean) as string[]
	const uniqueBuildIds = owner.Reaction.map((r) => r.buildId).filter(Boolean) as string[]
	const uniqueNewsIds = owner.Reaction.map((r) => r.newsPostId).filter(Boolean) as string[]
	const uniqueFanSiteIds = owner.Reaction.map((r) => r.fanSiteId).filter(Boolean) as string[]

	const [scriptDetails, guideDetails, buildDetails, newsDetails, fanSiteDetails] = await Promise.all([
		uniqueScriptIds.length > 0
			? prisma.script.findMany({ where: { id: { in: uniqueScriptIds } }, select: { id: true, title: true, slug: true } })
			: Promise.resolve([]),
		uniqueGuideIds.length > 0
			? prisma.guide.findMany({ where: { id: { in: uniqueGuideIds } }, select: { id: true, title: true, slug: true } })
			: Promise.resolve([]),
		uniqueBuildIds.length > 0
			? prisma.userBuildTemplate.findMany({ where: { id: { in: uniqueBuildIds } }, select: { id: true, name: true, slug: true, server: { select: { slug: true } } } })
			: Promise.resolve([]),
		uniqueNewsIds.length > 0
			? prisma.newsPost.findMany({ where: { id: { in: uniqueNewsIds } }, select: { id: true, title: true, slug: true } })
			: Promise.resolve([]),
		uniqueFanSiteIds.length > 0
			? prisma.fanSite.findMany({ where: { id: { in: uniqueFanSiteIds } }, select: { id: true, title: true, slug: true } })
			: Promise.resolve([]),
	])

	return json({ reactions: owner.Reaction, scriptDetails, guideDetails, buildDetails, newsDetails, fanSiteDetails })
}

const categoryStyles: Record<string, { label: string; className: string }> = {
	script: { label: 'Script', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
	guide: { label: 'Guide', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
	build: { label: 'Build', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
	news: { label: 'News', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' },
	fanSite: { label: 'Fan Site', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300' },
}

export default function ReactionTypeRoute() {
	const { reactions, scriptDetails, guideDetails, buildDetails, newsDetails, fanSiteDetails } = useLoaderData<typeof loader>()

	if (reactions.length === 0) {
		return (
			<div className="rounded-xl border bg-muted/30 p-6 text-center">
				<p className="text-sm text-muted-foreground">No reactions of this type yet.</p>
			</div>
		)
	}

	return (
		<div className="space-y-2">
			{reactions.map((reaction) => {
				const reactionTypeIcon = reactionTypes.find((rt) => rt.type === reaction.type)?.icon
				const timeAgo = formatDistanceToNow(new Date(reaction.createdAt), { addSuffix: true })

				let category: string | null = null
				let title: string | undefined
				let link: string | undefined

				if (reaction.scriptId) {
					category = 'script'
					const script = scriptDetails.find((s) => s?.id === reaction.scriptId)
					title = script?.title ?? 'Unknown script'
					link = `/script/${script?.slug ?? reaction.scriptId}`
				} else if (reaction.guideId) {
					category = 'guide'
					const guide = guideDetails.find((g) => g?.id === reaction.guideId)
					title = guide?.title ?? 'Unknown guide'
					link = `/guide/${guide?.slug ?? reaction.guideId}`
				} else if (reaction.buildId) {
					category = 'build'
					const build = buildDetails.find((b) => b?.id === reaction.buildId)
					title = build?.name ?? 'Unknown build'
					link = `/builds/${build?.server.slug}/${build?.slug ?? reaction.buildId}`
				} else if (reaction.newsPostId) {
					category = 'news'
					const newsPost = newsDetails.find((n) => n?.id === reaction.newsPostId)
					title = newsPost?.title ?? 'Unknown news post'
					link = `/news/${newsPost?.slug ?? reaction.newsPostId}`
				} else if (reaction.fanSiteId) {
					category = 'fanSite'
					const fanSite = fanSiteDetails.find((f) => f?.id === reaction.fanSiteId)
					title = fanSite?.title ?? 'Unknown fan site'
					link = `/fan-site/${fanSite?.slug ?? reaction.fanSiteId}`
				}

				if (!category || !link) return null

				const style = categoryStyles[category]!

				return (
					<div key={reaction.id} className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2.5 md:gap-3 md:px-4 md:py-3">
						<span className="text-base md:text-lg">{reactionTypeIcon}</span>
						<span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${style.className}`}>
							{style.label}
						</span>
						<Link to={link} className="flex-1 truncate text-sm font-medium hover:underline">
							{title}
						</Link>
						<span className="shrink-0 text-xs text-muted-foreground">{timeAgo}</span>
					</div>
				)
			})}
		</div>
	)
}

export const meta: MetaFunction<
	null,
	{ 'routes/users+/$username_+/reactions': typeof reactionsLoader }
> = ({ params, matches }) => {
	const match = matches.find((m) => m.id === 'routes/users+/$username_+/reactions')
	const displayName = match?.data?.owner.name ?? params.username
	return [
		{ title: `${displayName}'s ${params.type} Reactions | ${brandName}` },
		{ name: 'description', content: `${displayName}'s ${params.type} reactions on ${brandName}` },
	]
}
